import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Button, IconButton, MenuItem, TextField, Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteIcon,
  PersonAddAltOutlined as PersonAddIcon,
  RefreshOutlined as RefreshIcon,
  SwapHorizOutlined as TransferIcon
} from "@mui/icons-material";
import {
  PortfolioCreateFundUnitTransfer,
  PortfolioGetFundUnitTransfer,
  PortfolioListFundUnitTransfers,
  PortfolioListOnboardedUsers,
  PortfolioPreviewFundUnitTransfer
} from "../../util/API/portfolionew";
import "./UnitTransferManager.scss";

const newLine = () => ({ id: `${Date.now()}-${Math.random()}`, selectionId: "", destinationPositionId: "", units: "", pricePerUnit: "" });
const transferDecimalPattern = /^\d{1,12}(?:\.\d{1,8})?$/;
export const validateTransferDecimal = value => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (!transferDecimalPattern.test(text) || Number(text) <= 0) {
    return "Enter a value greater than zero with no more than 12 whole digits and 8 decimal places.";
  }
  return "";
};
const scaledTransferDecimal = value => {
  const text = String(value ?? "").trim();
  if (!text || validateTransferDecimal(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  return (window.BigInt(whole) * 100000000n) + window.BigInt(fraction.padEnd(8, "0"));
};
const considerationCentsFor = line => {
  const unitValue = scaledTransferDecimal(line.units);
  const priceValue = scaledTransferDecimal(line.pricePerUnit);
  if (unitValue === null || priceValue === null) return null;
  const scaledProduct = unitValue * priceValue;
  const centsDivisor = 100000000000000n;
  const wholeCents = scaledProduct / centsDivisor;
  const remainder = scaledProduct % centsDivisor;
  return wholeCents + (remainder * 2n >= centsDivisor ? 1n : 0n);
};
const amountFor = line => {
  const cents = considerationCentsFor(line);
  return cents === null ? Number(line.units || 0) * Number(line.pricePerUnit || 0) : Number(cents) / 100;
};
const sum = (items, selector) => items.reduce((total, item) => total + selector(item), 0);

const formatMoney = (value, currency = "USD") => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: currency || "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(Number(value) || 0);

const formatUnits = value => new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8
}).format(Number(value) || 0);

const selectedClassMatches = (position, unitClassId) => {
  if (!unitClassId) return true;
  return String(position.fundUnitClassId || "") === String(unitClassId);
};

const transferRequestId = () => window.crypto?.randomUUID?.()
  || `unit-transfer-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const errorMessages = value => {
  if (typeof value === "string" && value.trim()) return [value.trim()];
  if (Array.isArray(value)) return value.flatMap(errorMessages);
  if (value && typeof value === "object") return Object.values(value).flatMap(errorMessages);
  return [];
};
export const apiError = (error, fallback) => {
  const detailed = [...new Set(errorMessages(error?.response?.data?.errors))];
  if (detailed.length) return detailed.join(" ");
  return error?.response?.data?.message || error?.message || fallback;
};

const serverRows = preview => [
  ...(preview?.seller_rows || []),
  ...(preview?.buyer_rows || [])
].map(row => ({
  ...row,
  id: `${row.side}-${row.investor_fund_id || row.investor_id}`,
  name: row.investor_name,
  email: row.investor_email,
  currentUnits: Number(row.units_before || 0),
  transferUnits: Number(row.units || 0),
  resultingUnits: Number(row.units_after || 0),
  ownershipBefore: Number(row.ownership_before || 0),
  ownershipAfter: Number(row.ownership_after || 0),
  amount: Number(row.consideration_amount || 0)
}));

export const buildTransferPreview = ({ sellers, buyers, positions, users, unitClassId }) => {
  const totalFundUnits = sum(
    positions.filter(position => selectedClassMatches(position, unitClassId)),
    position => Number(position.units || 0)
  );

  const sellerRows = sellers.map(line => {
    const position = positions.find(item => String(item.id) === String(line.selectionId));
    const transferUnits = Number(line.units || 0);
    const currentUnits = Number(position?.units || 0);
    return {
      ...line,
      side: "seller",
      name: position?.ownershipDisplayName || position?.investorName || "Select seller",
      email: position?.investorEmail || position?.email || "",
      investorId: position?.investorId || null,
      currentUnits,
      transferUnits,
      resultingUnits: currentUnits - transferUnits,
      amount: amountFor(line),
      ownershipBefore: totalFundUnits > 0 ? currentUnits / totalFundUnits * 100 : 0,
      ownershipAfter: totalFundUnits > 0 ? (currentUnits - transferUnits) / totalFundUnits * 100 : 0,
      available: Boolean(position) && transferUnits > 0 && transferUnits <= currentUnits
    };
  });

  const buyerRows = buyers.map(line => {
    const user = users.find(item => String(item.id) === String(line.selectionId));
    const matchingPositions = positions.filter(position => String(position.investorId) === String(line.selectionId) && selectedClassMatches(position, unitClassId));
    const createsNewPosition = line.destinationPositionId === "new" || matchingPositions.length === 0;
    const destinationPosition = !createsNewPosition && line.destinationPositionId
      ? matchingPositions.find(position => String(position.id) === String(line.destinationPositionId))
      : !createsNewPosition && matchingPositions.length === 1 ? matchingPositions[0] : null;
    const destinationResolved = createsNewPosition || matchingPositions.length === 1 || Boolean(destinationPosition);
    const currentUnits = Number(destinationPosition?.units || 0);
    const transferUnits = Number(line.units || 0);
    return {
      ...line,
      side: "buyer",
      name: user?.name || "Select buyer",
      email: user?.email || "",
      investorId: user?.id || null,
      destinationPositionId: destinationPosition?.id || null,
      createsNewPosition,
      destinationResolved,
      matchingPositionCount: matchingPositions.length,
      currentUnits,
      transferUnits,
      resultingUnits: currentUnits + transferUnits,
      amount: amountFor(line),
      ownershipBefore: totalFundUnits > 0 ? currentUnits / totalFundUnits * 100 : 0,
      ownershipAfter: totalFundUnits > 0 ? (currentUnits + transferUnits) / totalFundUnits * 100 : 0
    };
  });

  const sellerUnits = sum(sellerRows, row => row.transferUnits);
  const buyerUnits = sum(buyerRows, row => row.transferUnits);
  const sellerAmount = sum(sellerRows, row => row.amount);
  const buyerAmount = sum(buyerRows, row => row.amount);
  const sellerUnitValues = sellers.map(line => scaledTransferDecimal(line.units));
  const buyerUnitValues = buyers.map(line => scaledTransferDecimal(line.units));
  const sellerConsiderations = sellers.map(considerationCentsFor);
  const buyerConsiderations = buyers.map(considerationCentsFor);
  const decimalsValid = [...sellerUnitValues, ...buyerUnitValues, ...sellerConsiderations, ...buyerConsiderations].every(value => value !== null);
  const sellerUnitsScaled = sellerUnitValues.reduce((total, value) => total + (value || 0n), 0n);
  const buyerUnitsScaled = buyerUnitValues.reduce((total, value) => total + (value || 0n), 0n);
  const sellerConsiderationCents = sellerConsiderations.reduce((total, value) => total + (value || 0n), 0n);
  const buyerConsiderationCents = buyerConsiderations.reduce((total, value) => total + (value || 0n), 0n);
  const selectedSellerIds = sellerRows.map(row => String(row.investorId || "")).filter(Boolean);
  const selectedBuyerIds = buyerRows.map(row => String(row.investorId || "")).filter(Boolean);
  const duplicateSeller = new Set(selectedSellerIds).size !== selectedSellerIds.length;
  const duplicateBuyer = new Set(selectedBuyerIds).size !== selectedBuyerIds.length;
  const selfTransfer = selectedSellerIds.some(id => selectedBuyerIds.includes(id));
  const completedLines = decimalsValid
    && sellerRows.every(row => row.investorId && row.available)
    && buyerRows.every(row => row.investorId && row.destinationResolved);

  return {
    sellerRows,
    buyerRows,
    sellerUnits,
    buyerUnits,
    sellerAmount,
    buyerAmount,
    unitDifference: buyerUnits - sellerUnits,
    amountDifference: buyerAmount - sellerAmount,
    unitsBalanced: decimalsValid && sellerUnitsScaled === buyerUnitsScaled,
    amountsBalanced: decimalsValid && sellerConsiderationCents === buyerConsiderationCents,
    decimalsValid,
    duplicateSeller,
    duplicateBuyer,
    selfTransfer,
    missingBuyerDestination: buyerRows.some(row => !row.destinationResolved),
    completedLines
  };
};

function TransferLineEditor({ side, lines, setLines, options, positions, unitClassId, currency }) {
  const update = (id, field, value) => setLines(current => current.map(line => line.id === id ? { ...line, [field]: value } : line));
  const remove = id => setLines(current => current.filter(line => line.id !== id));

  return <div className="yp-transfer-lines">
    {lines.map((line, index) => {
      const option = options.find(item => String(item.id) === String(line.selectionId));
      const availableUnits = side === "seller" ? Number(option?.units || 0) : null;
      const buyerPositions = side === "buyer" && option
        ? positions.filter(position => String(position.investorId) === String(option.id) && selectedClassMatches(position, unitClassId))
        : [];
      const destinationValue = buyerPositions.length > 1
        ? line.destinationPositionId
        : buyerPositions.length === 1 ? line.destinationPositionId || String(buyerPositions[0].id) : "new";
      const unitsError = validateTransferDecimal(line.units);
      const priceError = validateTransferDecimal(line.pricePerUnit);
      return <article key={line.id} className={side === "buyer" ? "buyer" : "seller"}>
        <div className="yp-transfer-line-number">{index + 1}</div>
        <Autocomplete
          className="yp-transfer-party-field"
          options={options}
          value={option || null}
          getOptionLabel={item => side === "seller"
            ? `${item.ownershipDisplayName || item.investorName}${item.unitClassName ? ` · ${item.unitClassName}` : ""}`
            : `${item.name}${item.email ? ` · ${item.email}` : ""}`}
          isOptionEqualToValue={(item, value) => String(item.id) === String(value.id)}
          onChange={(_, value) => setLines(current => current.map(item => item.id === line.id ? {
            ...item,
            selectionId: value ? String(value.id) : "",
            destinationPositionId: ""
          } : item))}
          renderInput={params => <TextField {...params} required label={side === "seller" ? "Seller position" : "Buyer"} helperText={side === "seller" && option ? `${formatUnits(availableUnits)} units available` : side === "buyer" && option ? option.email || "No email available" : ""} />}
        />
        {side === "buyer" && <TextField
          required={buyerPositions.length > 1}
          select={buyerPositions.length > 0}
          disabled={!option || buyerPositions.length === 0}
          label="Destination position"
          value={!option ? "" : destinationValue}
          onChange={event => update(line.id, "destinationPositionId", event.target.value)}
          error={Boolean(option) && buyerPositions.length > 1 && !line.destinationPositionId}
          helperText={!option
            ? "Select a buyer first"
            : buyerPositions.length === 0
              ? "A new transfer position will be created"
              : destinationValue === "new"
                ? "A separate position will be created on the effective date"
                : buyerPositions.length === 1
                  ? `${formatUnits(buyerPositions[0].units)} current units`
                  : line.destinationPositionId ? "Units will be added to this position" : "Select an existing position or create a new one"}
        >
          {buyerPositions.length > 0 && <MenuItem value="new">Create new transfer position</MenuItem>}
          {buyerPositions.length > 0 && buyerPositions.map(position => <MenuItem key={position.id} value={String(position.id)}>
            Position #{position.id} · {formatUnits(position.units)} units{position.joinedAt ? ` · Opened ${new Date(position.joinedAt).toLocaleDateString("en-US")}` : ""}
          </MenuItem>)}
        </TextField>}
        <TextField required type="number" label="Units" value={line.units} onChange={event => update(line.id, "units", event.target.value)} inputProps={{ min: 0.00000001, max: side === "seller" && option ? availableUnits : undefined, step: "0.00000001" }} error={Boolean(unitsError) || (side === "seller" && Number(line.units || 0) > availableUnits)} helperText={unitsError || (side === "seller" && Number(line.units || 0) > availableUnits ? "Exceeds available units" : "Up to 8 decimals")} />
        <TextField required type="number" label="Price per unit" value={line.pricePerUnit} onChange={event => update(line.id, "pricePerUnit", event.target.value)} inputProps={{ min: 0.00000001, step: "0.00000001" }} error={Boolean(priceError)} helperText={priceError || "Up to 8 decimals"} />
        <div className="yp-transfer-line-summary">
          <div className="yp-transfer-calculated"><span>Consideration</span><strong>{formatMoney(amountFor(line), currency)}</strong></div>
          <Tooltip title={lines.length === 1 ? `At least one ${side} is required` : `Remove ${side}`}><span className="yp-transfer-remove-line"><IconButton disabled={lines.length === 1} aria-label={`Remove ${side} ${index + 1}`} onClick={() => remove(line.id)}><DeleteIcon /></IconButton></span></Tooltip>
        </div>
      </article>;
    })}
    <Button startIcon={<AddIcon />} onClick={() => setLines(current => [...current, newLine()])}>Add {side}</Button>
  </div>;
}

function PreviewTable({ rows, currency, showOwnership = true }) {
  return <div className="yp-transfer-preview-table">
    <div><span>Participant</span><span>Side</span><span className="yp-transfer-number">Current units</span><span className="yp-transfer-number">Transfer</span><span className="yp-transfer-number">Resulting units</span><span className="yp-transfer-number">{showOwnership ? "Ownership" : "Position impact"}</span><span className="yp-transfer-number">Amount</span></div>
    {rows.map(row => <article key={`${row.side}-${row.id}`}>
      <div><strong>{row.name}</strong><small>{row.email || "No email available"}</small></div>
      <span className={`yp-transfer-side ${row.side}`}>{row.side}</span>
      <span className="yp-transfer-number">{formatUnits(row.currentUnits)}</span>
      <strong className="yp-transfer-number">{row.side === "seller" ? "−" : "+"}{formatUnits(row.transferUnits)}</strong>
      <strong className="yp-transfer-number">{formatUnits(row.resultingUnits)}</strong>
      <span className="yp-transfer-number">{showOwnership ? `${row.ownershipBefore.toFixed(4)}% → ${row.ownershipAfter.toFixed(4)}%` : `${row.side === "seller" ? "Reduced" : "Increased"} by ${formatUnits(row.transferUnits)} units`}</span>
      <strong className="yp-transfer-number">{formatMoney(row.amount, currency)}</strong>
    </article>)}
  </div>;
}

export default function UnitTransferManager({ fund, positions, unitClasses, actorId, onClose, onOnboardBuyer, onFinalized, onCountChange }) {
  const [view, setView] = useState("history");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [unitClassId, setUnitClassId] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [sellers, setSellers] = useState([newLine()]);
  const [buyers, setBuyers] = useState([newLine()]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userRefresh, setUserRefresh] = useState(0);
  const [transfers, setTransfers] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [serverPreview, setServerPreview] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const currency = fund?.currencyCode || fund?.currency_code || fund?.currency || "USD";
  const activeClasses = useMemo(() => unitClasses.filter(item => item.status === "active"), [unitClasses]);

  useEffect(() => {
    if (!unitClassId && activeClasses.length) {
      const preferred = activeClasses.find(item => item.is_default) || activeClasses[0];
      setUnitClassId(String(preferred.id));
    }
  }, [activeClasses, unitClassId]);

  useEffect(() => {
    if (!actorId) {
      setUsers([]);
      return undefined;
    }
    let mounted = true;
    setUsersLoading(true);
    PortfolioListOnboardedUsers(actorId).then(response => {
      if (!mounted) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      setUsers(rows.filter(user => String(user.id) !== String(actorId)));
    }).catch(() => {
      if (mounted) setUsers([]);
    }).finally(() => {
      if (mounted) setUsersLoading(false);
    });
    return () => { mounted = false; };
  }, [actorId, userRefresh]);

  const loadHistory = async () => {
    if (!fund?.id) return;
    setHistoryLoading(true);
    try {
      const response = await PortfolioListFundUnitTransfers(fund.id);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setTransfers(rows);
      if (onCountChange) onCountChange(rows.length);
    } catch (error) {
      setMessage({ severity: "error", text: apiError(error, "Unable to load unit-transfer history.") });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, [fund?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sellerOptions = useMemo(() => positions.filter(position => Number(position.units || 0) > 0 && selectedClassMatches(position, unitClassId)), [positions, unitClassId]);
  const preview = useMemo(() => buildTransferPreview({ sellers, buyers, positions, users, unitClassId }), [sellers, buyers, positions, users, unitClassId]);
  const selectedClass = activeClasses.find(item => String(item.id) === String(unitClassId));
  const changeSellers = update => { setSellers(update); setMessage(null); setServerPreview(null); };
  const changeBuyers = update => { setBuyers(update); setMessage(null); setServerPreview(null); };
  const formComplete = Boolean(effectiveDate)
    && (!activeClasses.length || Boolean(unitClassId))
    && preview.completedLines
    && preview.unitsBalanced
    && preview.amountsBalanced
    && !preview.duplicateSeller
    && !preview.duplicateBuyer
    && !preview.selfTransfer;

  const reset = () => {
    setEffectiveDate("");
    setReference("");
    setDescription("");
    setSellers([newLine()]);
    setBuyers([newLine()]);
    setServerPreview(null);
    setSelectedTransfer(null);
    setView("history");
  };

  const payload = includeFinalization => ({
    effective_date: effectiveDate,
    fund_unit_class_id: unitClassId || null,
    reference: reference.trim() || null,
    description: description.trim() || null,
    sellers: sellers.map(line => ({ investor_fund_id: Number(line.selectionId), units: String(line.units), price_per_unit: String(line.pricePerUnit) })),
    buyers: buyers.map(line => {
      const matchingPositions = positions.filter(position => String(position.investorId) === String(line.selectionId) && selectedClassMatches(position, unitClassId));
      const createNewPosition = line.destinationPositionId === "new" || matchingPositions.length === 0;
      const destinationPositionId = createNewPosition
        ? ""
        : line.destinationPositionId || (matchingPositions.length === 1 ? String(matchingPositions[0].id) : "");
      return {
        investor_id: Number(line.selectionId),
        investor_fund_id: destinationPositionId ? Number(destinationPositionId) : null,
        create_new_position: createNewPosition,
        units: String(line.units),
        price_per_unit: String(line.pricePerUnit)
      };
    }),
    created_by: Number(actorId),
    ...(includeFinalization ? { request_id: transferRequestId() } : {})
  });

  const previewTransfer = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await PortfolioPreviewFundUnitTransfer(fund.id, payload(false));
      setServerPreview(response?.data || null);
      setView("preview");
    } catch (error) {
      setMessage({ severity: "error", text: apiError(error, "The transfer could not be previewed.") });
    } finally {
      setSaving(false);
    }
  };

  const finalizeTransfer = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await PortfolioCreateFundUnitTransfer(fund.id, payload(true));
      await loadHistory();
      if (onFinalized) await onFinalized();
      reset();
      const delivery = response?.data?.email_delivery;
      const deliveryText = delivery?.status === "sent"
        ? ` ${Number(delivery.sent || 0)} participant email${Number(delivery.sent) === 1 ? "" : "s"} sent.`
        : delivery?.status === "skipped" && delivery?.reason === "no_active_template"
          ? " No active unit transfer email template was configured, so no emails were sent."
          : delivery?.status === "partially_sent" || delivery?.status === "failed"
            ? " Some participant emails could not be sent; delivery results were recorded in Email history."
            : "";
      setMessage({ severity: ["failed", "partially_sent"].includes(delivery?.status) ? "warning" : "success", text: `Unit transfer finalized successfully.${deliveryText}` });
    } catch (error) {
      setMessage({ severity: "error", text: apiError(error, "The unit transfer could not be finalized.") });
    } finally {
      setSaving(false);
    }
  };

  const viewTransfer = async transfer => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await PortfolioGetFundUnitTransfer(transfer.id);
      setSelectedTransfer(response?.data || transfer);
      setView("detail");
    } catch (error) {
      setMessage({ severity: "error", text: apiError(error, "The transfer details could not be loaded.") });
    } finally {
      setSaving(false);
    }
  };

  if (view === "history") return <section className="yp-management-section yp-transfer-management">
    <div className="yp-transfer-heading"><div><strong>Unit transfers</strong><p>Move units between investors while retaining a complete before-and-after audit trail.</p></div><Button className="yp-primary" startIcon={<TransferIcon />} onClick={() => setView("edit")}>Create unit transfer</Button></div>
    {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
    {historyLoading ? <div className="yp-transfer-empty"><RefreshIcon className="yp-spin" /><strong>Loading unit transfers</strong></div> : transfers.length ? <div className="yp-transfer-history">
      <div><span>Reference</span><span>Effective date</span><span>Participants</span><span>Units</span><span>Consideration</span><span>Status</span><span>Action</span></div>
      {transfers.map(transfer => <article key={transfer.id}>
        <div><strong>{transfer.reference}</strong><small>{transfer.unit_class_name || "Default class"}</small></div>
        <span>{transfer.effective_date}</span>
        <span>{Number(transfer.seller_count || 0)} seller{Number(transfer.seller_count) === 1 ? "" : "s"} · {Number(transfer.buyer_count || 0)} buyer{Number(transfer.buyer_count) === 1 ? "" : "s"}</span>
        <strong>{formatUnits(transfer.total_units)}</strong>
        <strong>{formatMoney(transfer.total_consideration, transfer.currency_code || currency)}</strong>
        <span className={`yp-transfer-status ${transfer.status}`}>{transfer.status}</span>
        <Button disabled={saving} onClick={() => viewTransfer(transfer)}>View</Button>
      </article>)}
    </div> : <div className="yp-transfer-empty"><TransferIcon /><strong>No unit transfers recorded</strong><span>Create a transfer to select sellers, buyers, units, and agreed prices.</span><Button variant="outlined" startIcon={<AddIcon />} onClick={() => setView("edit")}>Start transfer</Button></div>}
    <div className="yp-drawer-actions"><Button onClick={onClose}>Close</Button></div>
  </section>;

  if (view === "detail") {
    const detailRows = (selectedTransfer?.lines || []).map(row => ({
      ...row,
      id: row.id,
      name: row.investor_name_snapshot,
      email: row.investor_email_snapshot,
      currentUnits: Number(row.units_before || 0),
      transferUnits: Number(row.units || 0),
      resultingUnits: Number(row.units_after || 0),
      ownershipBefore: 0,
      ownershipAfter: 0,
      amount: Number(row.consideration_amount || 0)
    }));
    return <section className="yp-management-section yp-transfer-management">
      <div className="yp-transfer-editor-heading"><Button startIcon={<ArrowBackIcon />} onClick={() => setView("history")}>Transfer history</Button><div><span className="yp-eyebrow">Finalized transfer</span><strong>{selectedTransfer?.reference}</strong><p>Effective {selectedTransfer?.effective_date} · {selectedTransfer?.unit_class_name || "Default class"}</p></div></div>
      <div className="yp-transfer-balance-summary"><article className="matched"><span>Transferred units</span><strong>{formatUnits(selectedTransfer?.total_units)}</strong></article><article className="matched"><span>Consideration</span><strong>{formatMoney(selectedTransfer?.total_consideration, selectedTransfer?.currency_code || currency)}</strong></article><article className="matched"><span>Status</span><strong>{selectedTransfer?.status}</strong></article></div>
      <PreviewTable rows={detailRows} currency={selectedTransfer?.currency_code || currency} showOwnership={false} />
      <div className="yp-drawer-actions"><Button onClick={() => setView("history")}>Back</Button></div>
    </section>;
  }

  if (view === "preview") return <section className="yp-management-section yp-transfer-management">
    <div className="yp-transfer-editor-heading"><Button startIcon={<ArrowBackIcon />} onClick={() => setView("edit")}>Change details</Button><div><span className="yp-eyebrow">Transfer preview</span><strong>Review resulting ownership</strong><p>{fund.name} · {selectedClass?.name || "Default class"} · Effective {effectiveDate}</p></div></div>
    <div className="yp-transfer-balance-summary">
      <article className={preview.unitsBalanced ? "matched" : "mismatch"}><span>Seller units</span><strong>{formatUnits(preview.sellerUnits)}</strong><small>Buyer units {formatUnits(preview.buyerUnits)}</small></article>
      <article className={preview.amountsBalanced ? "matched" : "mismatch"}><span>Seller consideration</span><strong>{formatMoney(preview.sellerAmount, currency)}</strong><small>Buyer consideration {formatMoney(preview.buyerAmount, currency)}</small></article>
      <article className={preview.unitsBalanced && preview.amountsBalanced ? "matched" : "mismatch"}><span>Balance check</span><strong>{preview.unitsBalanced && preview.amountsBalanced ? "Matched" : "Difference found"}</strong><small>{formatUnits(preview.unitDifference)} units · {formatMoney(preview.amountDifference, currency)}</small></article>
    </div>
    <PreviewTable rows={serverRows(serverPreview)} currency={serverPreview?.currency_code || currency} />
    {message && <Alert severity={message.severity}>{message.text}</Alert>}
    <div className="yp-drawer-actions"><Button disabled={saving} onClick={reset}>Cancel transfer</Button><Button disabled={saving} onClick={() => setView("edit")}>Change details</Button><Button className="yp-primary" disabled={saving || !serverPreview} onClick={finalizeTransfer}>{saving ? "Finalizing…" : "Finalize transfer"}</Button></div>
  </section>;

  return <section className="yp-management-section yp-transfer-management">
    <div className="yp-transfer-editor-heading"><Button startIcon={<ArrowBackIcon />} onClick={reset}>Transfer history</Button><div><span className="yp-eyebrow">New unit transfer</span><strong>Define sellers and buyers</strong><p>Both unit totals and consideration totals must match before preview.</p></div></div>
    <div className="yp-transfer-details">
      {activeClasses.length > 0 && <TextField required select label="Unit class" value={unitClassId} onChange={event => { setUnitClassId(event.target.value); changeSellers([newLine()]); changeBuyers([newLine()]); }}>{activeClasses.map(item => <MenuItem key={item.id} value={String(item.id)}>{item.name} ({item.code})</MenuItem>)}</TextField>}
      <TextField required type="date" label="Effective date" value={effectiveDate} onChange={event => setEffectiveDate(event.target.value)} InputLabelProps={{ shrink: true }} helperText="The date ownership moves to the buyers" />
      <TextField label="Reference (optional)" value={reference} onChange={event => setReference(event.target.value)} inputProps={{ maxLength: 50 }} placeholder="e.g. TRF-2026-001" />
      <TextField className="full" multiline minRows={2} label="Description or notes (optional)" value={description} onChange={event => setDescription(event.target.value)} inputProps={{ maxLength: 1000 }} />
    </div>

    <div className="yp-transfer-section-heading"><div><span>01</span><p><strong>Who is selling?</strong>Select funded positions with available units in this class.</p></div><b>{formatUnits(preview.sellerUnits)} units · {formatMoney(preview.sellerAmount, currency)}</b></div>
    <TransferLineEditor side="seller" lines={sellers} setLines={changeSellers} options={sellerOptions} positions={positions} unitClassId={unitClassId} currency={currency} />

    <div className="yp-transfer-section-heading"><div><span>02</span><p><strong>Who is buying?</strong>Select onboarded users or create a new investor profile first.</p></div><div className="yp-transfer-section-actions"><b>{formatUnits(preview.buyerUnits)} units · {formatMoney(preview.buyerAmount, currency)}</b><Tooltip title="Refresh onboarded buyers"><span><IconButton disabled={usersLoading} aria-label="Refresh onboarded buyers" onClick={() => setUserRefresh(current => current + 1)}><RefreshIcon /></IconButton></span></Tooltip><Button startIcon={<PersonAddIcon />} onClick={onOnboardBuyer}>Onboard buyer</Button></div></div>
    {usersLoading && <Alert severity="info">Loading onboarded buyers…</Alert>}
    <TransferLineEditor side="buyer" lines={buyers} setLines={changeBuyers} options={users} positions={positions} unitClassId={unitClassId} currency={currency} />

    <div className="yp-transfer-live-balance">
      <span className={preview.unitsBalanced && preview.sellerUnits > 0 ? "matched" : ""}><b>Unit difference</b><strong>{formatUnits(preview.unitDifference)}</strong></span>
      <span className={preview.amountsBalanced && preview.sellerAmount > 0 ? "matched" : ""}><b>Amount difference</b><strong>{formatMoney(preview.amountDifference, currency)}</strong></span>
      <span><b>Participants</b><strong>{sellers.length} seller{sellers.length === 1 ? "" : "s"} · {buyers.length} buyer{buyers.length === 1 ? "" : "s"}</strong></span>
    </div>
    {preview.duplicateSeller && <Alert severity="error">The same beneficial owner cannot be added as more than one seller in a transfer.</Alert>}
    {preview.duplicateBuyer && <Alert severity="error">The same buyer cannot be added more than once.</Alert>}
    {preview.selfTransfer && <Alert severity="error">A participant cannot be both a seller and buyer in the same transfer.</Alert>}
    {preview.missingBuyerDestination && <Alert severity="error">Select the destination position for each buyer who has multiple positions in this unit class.</Alert>}
    {!preview.decimalsValid && <Alert severity="error">Units and price per unit must be greater than zero, with no more than 12 whole digits and 8 decimal places.</Alert>}
    {!preview.unitsBalanced && preview.sellerUnits > 0 && preview.buyerUnits > 0 && <Alert severity="warning">Buyer and seller unit totals must match.</Alert>}
    {!preview.amountsBalanced && preview.sellerAmount > 0 && preview.buyerAmount > 0 && <Alert severity="warning">Buyer and seller consideration totals must match.</Alert>}
    {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
    <div className="yp-drawer-actions"><Button disabled={saving} onClick={reset}>Cancel</Button><Button className="yp-primary" disabled={!formComplete || saving} onClick={previewTransfer}>{saving ? "Validating…" : "Preview transfer"}</Button></div>
  </section>;
}
